// features/dashboard/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  FiUsers, 
  FiDollarSign, 
  FiTrendingUp, 
  FiAlertCircle,
  FiPieChart,
  FiActivity,
  FiUserCheck,
  FiCreditCard,
  FiClock
} from "react-icons/fi";
import { RiExchangeFill } from "react-icons/ri";
import AdminRegister from "./AdminRegister";

const Dashboard = ({ dashboardData }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Format currency with Indian Rupees symbol
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Calculate payout status
  const payoutDifference = dashboardData.dailyTotalPayout - dashboardData.todayPayoutAmount;
  const payoutStatus = Math.abs(payoutDifference) > 0.01 ? 'mismatch' : 'match';

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">
          {new Date().toLocaleDateString('en-IN', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
  {[
    {
      title: "Total Users",
      value: dashboardData.totalUsers,
      icon: <FiUsers className="w-5 h-5" />,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: "Total Account Balance",
      value: formatCurrency(dashboardData.totalAccountBalance),
      icon: <FiDollarSign className="w-5 h-5" />,
      color: "bg-purple-100 text-purple-600"
    },
    {
      title: "Today's Daily Credit",
      value: formatCurrency(dashboardData.todayPayoutAmount),
      icon: <FiClock className="w-5 h-5" />,
      color: "bg-amber-100 text-amber-600"
    }
  ].map((metric, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 transition-all duration-200"
    >
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium text-gray-500">{metric.title}</p>
          <p className="text-2xl font-semibold text-gray-800 mt-2">
            {typeof metric.value === "number"
              ? metric.value.toLocaleString("en-IN")
              : metric.value}
          </p>
        </div>
        <div className={`p-3 rounded-full ${metric.color}`}>
          {metric.icon}
        </div>
      </div>
    </motion.div>
  ))}
</div>


      {/* Investment Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          {
            title: "Total Investments",
            value: dashboardData.totalInvestments,
            icon: <FiTrendingUp className="w-5 h-5" />,
            color: "bg-indigo-100 text-indigo-600"
          },
          {
            title: "Total Investment Amount",
            value: formatCurrency(dashboardData.totalInvestmentAmount),
            icon: <FiDollarSign className="w-5 h-5" />,
            color: "bg-teal-100 text-teal-600"
          },
          {
            title: "Active Investments",
            value: dashboardData.activeInvestments,
            icon: <FiActivity className="w-5 h-5" />,
            color: "bg-green-100 text-green-600"
          },
          {
            title: "Completed Investments",
            value: dashboardData.completedInvestments,
            icon: <FiPieChart className="w-5 h-5" />,
            color: "bg-purple-100 text-purple-600"
          }
        ].map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
          >
            <div className="flex justify-between">
              <div>
                <p className="text-sm text-gray-500">{metric.title}</p>
                <p className="text-xl font-bold mt-1">
                  {typeof metric.value === 'number' 
                    ? metric.value.toLocaleString('en-IN') 
                    : metric.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${metric.color}`}>
                {metric.icon}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Transaction Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          {
            title: "Total Transactions",
            value: dashboardData.totalTransaction,
            icon: <FiActivity className="w-5 h-5" />,
            color: "bg-blue-100 text-blue-600"
          },
          {
            title: "Total Recharge Amount",
            value: formatCurrency(dashboardData.totalRechargePaid),
            icon: <FiCreditCard className="w-5 h-5" />,
            color: "bg-green-100 text-green-600"
          },
          {
            title: "Today's Recharge",
            value: formatCurrency(dashboardData.todayRechargeAmount),
            icon: <FiClock className="w-5 h-5" />,
            color: "bg-amber-100 text-amber-600"
          },
          {
            title: "Total Withdrawal",
            value: formatCurrency(dashboardData.totalWithdrawalAmount),
            icon: <FiDollarSign className="w-5 h-5" />,
            color: "bg-red-100 text-red-600"
          }
        ].map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
          >
            <div className="flex justify-between">
              <div>
                <p className="text-sm text-gray-500">{metric.title}</p>
                <p className="text-xl font-bold mt-1">
                  {typeof metric.value === 'number' 
                    ? metric.value.toLocaleString('en-IN') 
                    : metric.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${metric.color}`}>
                {metric.icon}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Payout and Commission */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Payout Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <RiExchangeFill className={`mr-2 ${payoutStatus === 'mismatch' ? 'text-red-600' : 'text-green-600'}`} />
            Payout Status
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Expected Payout</span>
              <span className="text-sm font-medium">{formatCurrency(dashboardData.dailyTotalPayout)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Processed Today</span>
              <span className="text-sm font-medium">{formatCurrency(dashboardData.todayPayoutAmount)}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full ${payoutStatus === 'mismatch' ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ 
                  width: `${(dashboardData.todayPayoutAmount / dashboardData.dailyTotalPayout) * 100}%` 
                }}
              ></div>
            </div>
            <div className="flex justify-between pt-2">
              <span className="text-sm text-gray-500">Difference</span>
              <span className={`text-sm font-medium ${
                payoutStatus === 'mismatch' ? 'text-red-600' : 'text-gray-700'
              }`}>
                {formatCurrency(payoutDifference)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Commission Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 lg:col-span-2"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <FiDollarSign className="mr-2 text-indigo-600" />
            Commission Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Total User Commission</p>
              <p className="font-semibold">{formatCurrency(dashboardData.totalUserCommission)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Recharge User Commission</p>
              <p className="font-semibold">{formatCurrency(dashboardData.totalRechargeUserCommission)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Referrer Commission</p>
              <p className="font-semibold">{formatCurrency(dashboardData.totalRechargeReferrerCommission)}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Admin Register Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <AdminRegister />
      </motion.div>
    </div>
  );
};

export default Dashboard;